/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Inventor Automation team
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////

import React, { Component } from 'react';
import { connect } from 'react-redux';
import BaseTable, { AutoResizer, Column } from 'react-base-table';
import 'react-base-table/styles.css';
import { getActiveProject, downloadProgressShowing, downloadProgressTitle, downloadUrl, downloadFailedShowing, errorData } from '../reducers/mainReducer';
import { downloadDrawingFailedShowing } from '../reducers/mainReducer';
import { getDownloadLink } from '../actions/downloadActions';
import { showDownloadProgress, showDownloadFailed } from '../actions/uiFlagsActions';
import ModalDownloadProgress from './modalDownloadProgress';
import ModalFail from './modalFail';
import settingsService from '../services/settingsService';

import repo from '../Repository';

const Icon = ({ iconname }) => (
    <div>
      <img src={iconname} alt=''/>
    </div>
);

const iconRenderer = ({ cellData: iconname }) => <Icon iconname={iconname} />;

/** Hyperlink that leads nowhere. */
function deadEndLink(title) {
    return <a href='' onClick={(e) => { e.preventDefault(); }}>{ title }</a>;
}

export const downloadColumns = [
    {
        key: 'icon',
        title: '',
        dataKey: 'icon',
        cellRenderer: iconRenderer,
        align: Column.Alignment.RIGHT,
        width: 100,
    },
    {
        key: 'type',
        title: 'File Type',
        dataKey: 'type',
        cellRenderer: ( { rowData } ) => rowData.link,
        align: Column.Alignment.LEFT,
        width: 150,
    },
    {
        key: 'env',
        title: 'Environment',
        dataKey: 'env',
        align: Column.Alignment.CENTER,
        width: 200,
    }
];

// add code to download URLs if necessary
function getDownloadUrls(project) {

    const { modelDownloadUrl, bomDownloadUrl } = project;

    const code = repo.getAccessCode();
    const suffix = code ? "/" + code : "";

    const configDownloadUrl = project.id && project.hash
        ? `download/${encodeURIComponent(project.id)}/${encodeURIComponent(project.hash)}/configuration${suffix}`
        : null;

    return {
        modelDownloadUrl: modelDownloadUrl ? modelDownloadUrl + suffix : modelDownloadUrl,
        bomDownloadUrl: bomDownloadUrl ? bomDownloadUrl + suffix : bomDownloadUrl,
        configDownloadUrl
    };
}

export class Downloads extends Component {
    constructor(props) {
        super(props);
        this.state = {
            downloadSettings: {
                modelEnabled: true,
                rfaEnabled: true,
                bomEnabled: true,
                drawingEnabled: true,
                pdfEnabled: true,
                configurationEnabled: true,
                stepEnabled: false,
                satEnabled: false,
                dwg2dEnabled: false,
                dwg3dEnabled: false
            },
            settingsLoaded: false
        };
    }

    async componentDidMount() {
        try {
            const downloadSettings = await settingsService.getDownloadSettings();
            this.setState({ downloadSettings, settingsLoaded: true });
        } catch (err) {
            // Use default settings (all enabled) if fetch fails
            this.setState({ settingsLoaded: true });
        }
    }

    render() {
        const { downloadSettings } = this.state;

        // array with rows data
        const data = [];

        const project = this.props.activeProject;
        
        // DEBUG: Log what we're getting
        console.log('Downloads - activeProject:', project);
        console.log('Downloads - modelDownloadUrl:', project?.modelDownloadUrl);
        console.log('Downloads - bomDownloadUrl:', project?.bomDownloadUrl);
        console.log('Downloads - hasDrawing:', project?.hasDrawing);
        console.log('Downloads - isAssembly:', project?.isAssembly);
        
        if (project?.id) {

            const { modelDownloadUrl, bomDownloadUrl, configDownloadUrl } = getDownloadUrls(project);
            
            console.log('Downloads - processed modelDownloadUrl:', modelDownloadUrl);
            console.log('Downloads - processed bomDownloadUrl:', bomDownloadUrl);
            
            if (modelDownloadUrl && downloadSettings.modelEnabled) {

                let downloadHyperlink;
                const modelJsx = <a href={modelDownloadUrl} onClick={(e) => { e.stopPropagation(); }} ref = {(h) => {
                    downloadHyperlink = h;
                }}>IAM/IPT</a>;

                // register the row
                data.push({
                    id: 'updatedIam',
                    icon: 'products-and-services-24.svg',
                    type: 'IAM',
                    env: 'Model',
                    link: modelJsx,
                    clickHandler: () => downloadHyperlink.click()
                });
            }

            if (downloadSettings.rfaEnabled) {
                data.push({
                    id: 'rfa',
                    icon: 'products-and-services-24.svg',
                    type: 'RFA',
                    env: 'Model',
                    link: deadEndLink('RFA'),
                    clickHandler: async () => this.props.getDownloadLink('CreateRFAJob', project.id, project.hash, 'Preparing RFA')
                });
            }

            if (bomDownloadUrl && project.isAssembly && downloadSettings.bomEnabled) {

                let downloadHyperlink;
                const bomJsx = <a href={bomDownloadUrl} onClick={(e) => { e.stopPropagation(); }} ref = {(h) => {
                    downloadHyperlink = h;
                }}>BOM (.csv File)</a>;

                // register the row
                data.push({
                    id: 'bom',
                    icon: 'file-spreadsheet-24.svg',
                    type: 'BOM',
                    env: 'BOM',
                    link: bomJsx,
                    clickHandler: () => downloadHyperlink.click()
                });
            }

            const hasDrawingUrl = project.hasDrawing;
            if (hasDrawingUrl && downloadSettings.drawingEnabled) {
                data.push(
                    {
                        id: 'drawing',
                        icon: 'products-and-services-24.svg',
                        type: 'IDW',
                        env: 'Drawing',
                        link: deadEndLink('Drawing'),
                        clickHandler: async () => this.props.getDownloadLink('CreateDrawingDownloadJob', project.id, project.hash, 'Preparing Drawings')
                    });
            }

            if (hasDrawingUrl && downloadSettings.pdfEnabled) {
                data.push({
                    id: 'pdf',
                    icon: 'products-and-services-24.svg',
                    type: 'PDF',
                    env: 'Drawing',
                    link: deadEndLink('PDF'),
                    clickHandler: async () => this.props.getDownloadLink('CreateDrawingPdfJob', project.id, project.hash, 'Preparing PDF', '')
                });
            }

            if (configDownloadUrl && downloadSettings.configurationEnabled) {
                let configHyperlink;
                const configJsx = <a href={configDownloadUrl} onClick={(e) => { e.stopPropagation(); }} ref={(h) => {
                    configHyperlink = h;
                }}>Configuration (.json)</a>;

                data.push({
                    id: 'configuration',
                    icon: 'file-spreadsheet-24.svg',
                    type: 'JSON',
                    env: 'Configuration',
                    link: configJsx,
                    clickHandler: () => configHyperlink.click()
                });
            }

            if (downloadSettings.satEnabled) {
                data.push({
                    id: 'sat',
                    icon: 'products-and-services-24.svg',
                    type: 'SAT',
                    env: 'Model',
                    link: deadEndLink('SAT (.sat File)'),
                    clickHandler: async () => this.props.getDownloadLink('CreateSATJob', project.id, project.hash, 'Preparing SAT')
                });
            }

            if (downloadSettings.stepEnabled) {
                data.push({
                    id: 'step',
                    icon: 'products-and-services-24.svg',
                    type: 'STEP',
                    env: 'Model',
                    link: deadEndLink('STEP (.stp File)'),
                    clickHandler: async () => this.props.getDownloadLink('CreateSTEPJob', project.id, project.hash, 'Preparing STEP')
                });
            }

            if (downloadSettings.dwg2dEnabled) {
                data.push({
                    id: 'dwg2d',
                    icon: 'products-and-services-24.svg',
                    type: '2D DWG',
                    env: 'Drawing',
                    link: deadEndLink('2D DWG (.dwg File)'),
                    clickHandler: async () => this.props.getDownloadLink('CreateDwg2dJob', project.id, project.hash, 'Preparing 2D DWG')
                });
            }

            if (downloadSettings.dwg3dEnabled) {
                data.push({
                    id: 'dwg3d',
                    icon: 'products-and-services-24.svg',
                    type: '3D DWG',
                    env: 'Model',
                    link: deadEndLink('3D DWG (.dwg File)'),
                    clickHandler: async () => this.props.getDownloadLink('CreateDwg3dJob', project.id, project.hash, 'Preparing 3D DWG')
                });
            }
        }
        
        console.log('Downloads - final data array:', data);

        return (
        <React.Fragment>
            <AutoResizer>
                {({ width, height }) => {
                    // reduce size by 16 (twice the default border of tabContent)
                    const newWidth = width-16;
                    const newHeight = height-16;
                    return <BaseTable
                        width={newWidth}
                        height={newHeight}
                        columns={downloadColumns}
                        data={data}
                        rowEventHandlers={{
                            onClick: ({ rowData }) => { rowData.clickHandler(); }
                        }}
                    />;
                }}
            </AutoResizer>
                {this.props.downloadProgressShowing && <ModalDownloadProgress
                    open={true}
                    title={ this.props.downloadProgressTitle }
                    label={project.id}
                    onClose={ () => this.props.showDownloadProgress(false) }
                    url={ this.props.downloadUrl } />}

                {this.props.downloadFailedShowing && <ModalFail
                    open={true}
                    title={ `${this.props.downloadProgressTitle} Failed` }
                    contentName="Project:"
                    label={project.id}
                    onClose={ () => this.props.showDownloadFailed(false) }
                    errorData={this.props.errorData}/>}

        </React.Fragment>
        );
    }
}

/* istanbul ignore next */
export default connect(function(store) {
    const activeProject = getActiveProject(store);
    return {
        activeProject: activeProject,
        downloadProgressShowing: downloadProgressShowing(store),
        downloadProgressTitle: downloadProgressTitle(store),
        downloadFailedShowing: downloadFailedShowing(store),
        downloadUrl: downloadUrl(store),
        errorData: errorData(store),
        drawingDownloadFailedShowing: downloadDrawingFailedShowing(store)
    };
}, { Downloads, getDownloadLink, showDownloadProgress, showDownloadFailed })(Downloads);